import { formatDateDE } from "@/lib/playerConstants";

export const ARTEN = [
  { key: "mannschaftstraining", label: "Mannschaftstraining", short: "Mannschaft" },
  { key: "bonustraining", label: "Bonustraining", short: "Bonus" },
];

export const STATUS_OPTIONS = [
  { key: "anwesend", label: "Anwesend", color: "bg-emerald-500", textColor: "text-emerald-400", border: "border-emerald-500/40 bg-emerald-500/10 text-emerald-400" },
  { key: "entschuldigt", label: "Entschuldigt", color: "bg-amber-500", textColor: "text-amber-400", border: "border-amber-500/40 bg-amber-500/10 text-amber-400" },
  { key: "unentschuldigt", label: "Unentschuldigt", color: "bg-rose-500", textColor: "text-rose-400", border: "border-rose-500/40 bg-rose-500/10 text-rose-400" },
];

export const REASON_OPTIONS = [
  { key: "verletzung", label: "Verletzung" },
  { key: "krankheit", label: "Krankheit" },
  { key: "schule", label: "Schule" },
  { key: "urlaub", label: "Urlaub" },
  { key: "klassenfahrt", label: "Klassenfahrt" },
  { key: "geburtstag", label: "Geburtstag" },
  { key: "wetter", label: "Wetter" },
  { key: "sonstiges", label: "Sonstiges" },
];

export const WEEKDAYS = [
  { idx: 0, label: "Montag", short: "Mo" },
  { idx: 1, label: "Dienstag", short: "Di" },
  { idx: 2, label: "Mittwoch", short: "Mi" },
  { idx: 3, label: "Donnerstag", short: "Do" },
  { idx: 4, label: "Freitag", short: "Fr" },
  { idx: 5, label: "Samstag", short: "Sa" },
  { idx: 6, label: "Sonntag", short: "So" },
];

export function artLabel(key) {
  return ARTEN.find((a) => a.key === key)?.label || key;
}

export function statusLabel(key) {
  return STATUS_OPTIONS.find((s) => s.key === key)?.label || "—";
}

export function reasonLabel(key) {
  return REASON_OPTIONS.find((r) => r.key === key)?.label || key;
}

export function weekdayOfIso(iso) {
  // JS getDay: Sunday=0..Saturday=6 — convert to Monday=0
  const d = new Date(iso + "T00:00:00");
  return (d.getDay() + 6) % 7;
}

export function weekdayLabelOfIso(iso) {
  return WEEKDAYS[weekdayOfIso(iso)]?.short || "";
}

export { formatDateDE };

// Count attendance per training
export function countAttendance(training) {
  const c = { anwesend: 0, entschuldigt: 0, unentschuldigt: 0, total_recorded: 0 };
  for (const v of Object.values(training.attendance || {})) {
    if (c[v.status] !== undefined) {
      c[v.status]++;
      c.total_recorded++;
    }
  }
  return c;
}

// Compute stats for a single player across trainings
export function playerStats(playerId, trainings) {
  let total = 0;
  const counts = { anwesend: 0, entschuldigt: 0, unentschuldigt: 0 };
  const reasonCounts = { verletzung: 0, krankheit: 0, schule: 0, urlaub: 0, sonstiges: 0 };
  const byArt = {
    mannschaftstraining: { total: 0, anwesend: 0 },
    bonustraining: { total: 0, anwesend: 0 },
  };

  // For streak calculation: pass-through in chronological order
  const sortedTrainings = [...trainings].sort((a, b) =>
    (a.datum + a.uhrzeit).localeCompare(b.datum + b.uhrzeit)
  );

  for (const t of sortedTrainings) {
    const entry = (t.attendance || {})[playerId];
    if (!entry) continue;
    total++;
    counts[entry.status] = (counts[entry.status] || 0) + 1;
    if (entry.status === "entschuldigt") {
      const r = entry.reason || "sonstiges";
      reasonCounts[r] = (reasonCounts[r] || 0) + 1;
    }
    if (byArt[t.art]) {
      byArt[t.art].total++;
      if (entry.status === "anwesend") byArt[t.art].anwesend++;
    }
  }

  const quote = total > 0 ? Math.round((counts.anwesend / total) * 1000) / 10 : 0;

  // Streaks
  let bestStreak = 0;
  let currentStreak = 0;
  let runningStreak = 0;
  for (const t of sortedTrainings) {
    const entry = (t.attendance || {})[playerId];
    if (!entry) continue;
    if (entry.status === "anwesend") {
      runningStreak++;
      if (runningStreak > bestStreak) bestStreak = runningStreak;
    } else {
      runningStreak = 0;
    }
  }
  currentStreak = runningStreak;

  const byArtWithQuote = {};
  for (const art of Object.keys(byArt)) {
    const { total: tot, anwesend: anw } = byArt[art];
    byArtWithQuote[art] = {
      total: tot,
      anwesend: anw,
      quote: tot > 0 ? Math.round((anw / tot) * 1000) / 10 : 0,
    };
  }

  return {
    total,
    counts,
    quote,
    reasonCounts,
    byArt: byArtWithQuote,
    currentStreak,
    bestStreak,
  };
}

// Team-level + season aggregation
export function teamStats(players, trainings) {
  const perPlayer = players.map((p) => ({ player: p, stats: playerStats(p.id, trainings) }));
  const totalTrainings = trainings.length;

  let totalAnw = 0, totalEnt = 0, totalUnent = 0;
  const reasonTotals = { verletzung: 0, krankheit: 0, schule: 0, urlaub: 0, sonstiges: 0 };
  for (const t of trainings) {
    const c = countAttendance(t);
    totalAnw += c.anwesend;
    totalEnt += c.entschuldigt;
    totalUnent += c.unentschuldigt;
    for (const v of Object.values(t.attendance || {})) {
      if (v.status === "entschuldigt") {
        const r = v.reason || "sonstiges";
        reasonTotals[r] = (reasonTotals[r] || 0) + 1;
      }
    }
  }

  const withRecords = perPlayer.filter((p) => p.stats.total > 0);
  const avgQuote =
    withRecords.length > 0
      ? Math.round(
          (withRecords.reduce((s, p) => s + p.stats.quote, 0) / withRecords.length) * 10
        ) / 10
      : 0;

  const ranking = [...perPlayer].sort((a, b) => {
    if (b.stats.quote !== a.stats.quote) return b.stats.quote - a.stats.quote;
    return b.stats.total - a.stats.total;
  });

  const best = withRecords.length > 0
    ? withRecords.reduce((acc, p) => (p.stats.quote > acc.stats.quote ? p : acc))
    : null;
  const worst = withRecords.length > 0
    ? withRecords.reduce((acc, p) => (p.stats.quote < acc.stats.quote ? p : acc))
    : null;

  const longestStreakOverall = perPlayer.reduce(
    (acc, p) => (p.stats.bestStreak > acc.streak ? { name: p.player.name, streak: p.stats.bestStreak } : acc),
    { name: "—", streak: 0 }
  );

  const artCounts = {
    mannschaftstraining: trainings.filter((t) => t.art === "mannschaftstraining").length,
    bonustraining: trainings.filter((t) => t.art === "bonustraining").length,
  };

  return {
    totalTrainings,
    totalAnw,
    totalEnt,
    totalUnent,
    avgQuote,
    ranking,
    best,
    worst,
    longestStreakOverall,
    reasonTotals,
    artCounts,
  };
}
